import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  CardContent,
  Box,
  IconButton,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import axios from "axios";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Redux Action Import
import { setHighlightSegment } from "features/routeReplay/routeReplaySlice";

let globalTripData = null;
let globalTripTitle = "";

const SpeedAnalysis = ({ onDrawerStateChange, isInsideSidebar }) => {
  const [refresh, setRefresh] = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null); // Hover state tracking
  const dispatch = useDispatch();

  const vehicleId = useSelector((state) => state.vehicle.routeReplayVehicleId);
  const storedTimes = useSelector((state) => state.vehicle.selectedStartTime);
  const endTimeStored = useSelector((state) => state.vehicle.selectedEndTime);
  const routeReplay = useSelector((state) => state.routeReplay);
  const devices = Object.values(routeReplay?.devices?.devices || {});
  const deviceLength = Object.keys(routeReplay?.devices?.devices || {}).length;

  React.useEffect(() => {
    if (deviceLength === 0) {
      onDrawerStateChange(false);
    }
  }, [deviceLength]);

  const speedRanges = { "< 45": 0, "45 - 60": 0, "60 - 75": 0, "> 75": 0 };
  devices.forEach((d) => {
    let speed = (d?.speed ?? 0) * 3.6;
    if (speed < 45) speedRanges["< 45"]++;
    else if (speed >= 45 && speed < 60) speedRanges["45 - 60"]++;
    else if (speed >= 60 && speed < 75) speedRanges["60 - 75"]++;
    else speedRanges["> 75"]++;
  });

  const chartData = Object.keys(speedRanges).map((range) => ({
    speed: range,
    percent: parseFloat(((speedRanges[range] / (devices.length || 1)) * 100).toFixed(2)),
  }));

 const handleFetch = async (type) => {
  const token = localStorage.getItem("token");
  
  // Pehle: yahan 'today' hardcoded tha.
  // Ab: hum wahi date nikalenge jo Route Replay ke start time me hai.
  const startDate = storedTimes ? storedTimes.split("T")[0] : new Date().toISOString().split("T")[0];
  const endDate = endTimeStored ? endTimeStored.split("T")[0] : startDate;
  const formatForHalt = (ts) => ts?.replace(/-/g, "/").replace("T", " ") || "";

  try {
    let response;
    if (type === "HALT") {
      response = await axios.post("https://v3.dhundhoo.com/reports/haltTripReport", {
        date: '',
        // date: selectedDate.replace(/-/g, "/"),
        zone: "Asia/Kolkata",
        startTime: formatForHalt(storedTimes),
        endTime: formatForHalt(endTimeStored),
        things: vehicleId,
        interval: 0,
        thingIds: [], geofence: [], groups: [], orgName: "", geoFenceName: "", geoLat: 0, geoLong: 0,
      }, { headers: { "x-dhundhoo-session": token } });
      globalTripTitle = "Halt Trip Analysis";
    } else {
      // Ignition Trip API me bhi wahi date bhejein jo filter me select ki hai
      response = await axios.post(`https://v3.dhundhoo.com/reports/ignitionTripReport/${vehicleId}`,
        { startDate: startDate, endDate: endDate },
        { headers: { "x-dhundhoo-session": token } });
      globalTripTitle = "Ignition Trip Analysis";
    }
    globalTripData = response.data;
    onDrawerStateChange(true);
    setRefresh(prev => prev + 1);
  } catch (err) {
    console.error(err);
  }
};

  // Inside SpeedAnalysis.js

const handleRowClick = (trip) => {
  // Debugging ke liye: console me check karein keys kya hain
  console.log("Clicked Trip Keys:", Object.keys(trip));

  const segment = {
    // Ignition API me aksar 'ignStartTimeDate' hota hai, jabki Halt me 'startTime'
    startTime: trip.startTime || trip.ignStartTimeDate || trip.ignStartTime,
    endTime: trip.endTime || trip.ignEndTimeDate || trip.ignEndTime,
    type: globalTripTitle.toLowerCase().includes("ignition") ? "IGNITION" : "HALT",
    startLat: Number(trip.startTimeLatitude || trip.startLatitude || trip.ignStartLatitude || trip.haltLatitude),
    startLng: Number(trip.startTimeLongitude || trip.startLongitude || trip.ignStartLongitude || trip.haltLongitude),
  };

  console.log("Segment Prepared for Highlight:", segment);
  dispatch(setHighlightSegment(segment));
};

// Table row me is tarah call karein:
// onClick={() => handleRowClick(trip, globalTripTitle.includes("Ignition") ? "IGNITION" : "HALT")}

  // Logic to open Google Maps in a new tab
  const handleLocationClick = (e, lat, lng) => {
    e.stopPropagation(); // Prevents row click from triggering
    if (lat && lng) {
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };

  const handleClose = () => {
  // 1. Clear the highlighted segment in Redux
  dispatch(setHighlightSegment(null)); 
  
  // 2. Close the drawer/table
  onDrawerStateChange(false);
};

  if (isInsideSidebar) {
    return (
      <MDBox p={2} pt={0}>
        <Card sx={{ boxShadow: "none", border: "1px solid #f0f2f5" }}>
          <CardContent sx={{ p: 2 }}>
            <MDBox display="flex" gap={1} mb={2}>
              <MDButton variant="gradient" color="info" fullWidth size="small" onClick={() => handleFetch("HALT")}>Halt Trip</MDButton>
              <MDButton variant="gradient" color="dark" fullWidth size="small" onClick={() => handleFetch("IGNITION")}>Ignition Trip</MDButton>
            </MDBox>
            <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>📈 Speed Distribution</MDTypography>
            <Box sx={{ height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barSize={60}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="speed" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ fontSize: "0.75rem", borderRadius: "8px" }} />
                  <Bar dataKey="percent" radius={[6, 6, 0, 0]}>
                    {chartData.map((e, i) => (
                      <Cell key={i} fill={e.speed === "< 45" ? "#2bbf9f" : e.speed === "45 - 60" ? "#fba400" : "#ff0000"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </MDBox>
    );
  }

  return (
    <MDBox
      sx={{
        flex: 1,
        bgcolor: "white",
        borderRadius: "24px",
        boxShadow: "0 -4px 12px rgba(0,0,0,0.05)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "12px 24px",
        background: "#f8f9fa",
        borderBottom: "1px solid #ddd"
      }}>
        <span style={{ fontSize: "12px", fontWeight: "bold", color: "#344767" }}>
          {globalTripTitle}
        </span>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon sx={{ fontSize: "18px" }} />
        </IconButton>
      </div>

      <div style={{ flex: 1, overflowY: "auto", width: "100%" }}>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed"
        }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr style={{ background: "#f8f9fa" }}>
              <th style={{ width: "10%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Trip ID</th>
              <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>START TIME</th>
              <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>START LOCATION</th>
              <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>END TIME</th>
              <th style={{ width: "22.5%", padding: "12px", fontSize: "11px", textAlign: "left", borderBottom: "1px solid #ddd" }}>END LOCATION</th>
            </tr>
          </thead>
          <tbody>
            {globalTripData && Object.entries(globalTripData).map(([id, trip]) => {
              const sLat = trip.startTimeLatitude || trip.startLatitude || trip.ignStartLatitude || trip.haltLatitude;
              const sLng = trip.startTimeLongitude || trip.startLongitude || trip.ignStartLongitude || trip.haltLongitude;
              const eLat = trip.endTimeLatitude || trip.endLatitude || trip.ignEndLatitude || trip.haltLatitude;
              const eLng = trip.endTimeLongitude || trip.endLongitude || trip.ignEndLongitude || trip.haltLongitude;

              return (
                <tr
                  key={id}
                  onClick={() => handleRowClick(trip)}
                  onMouseEnter={() => setHoveredRow(id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    backgroundColor: hoveredRow === id ? "#f0f7ff" : "transparent",
                    transition: "background-color 0.2s ease"
                  }}
                >
                  <td style={{ padding: "12px", fontSize: "11px", width: "10%" }}>{id}</td>
                  <td style={{ padding: "12px", fontSize: "11px", width: "22.5%" }}>{trip.startTime}</td>
                  <td style={{ padding: "12px", width: "22.5%" }}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      onClick={(e) => handleLocationClick(e, sLat, sLng)}
                      sx={{ "&:hover": { opacity: 0.7 } }}
                    >
                      <LocationOnIcon sx={{ fontSize: "14px", color: "#1A73E8" }} />
                      <MDTypography variant="caption" color="info" fontWeight="medium" sx={{ fontSize: "11px" }}>
                        View Start
                      </MDTypography>
                    </MDBox>
                  </td>
                  <td style={{ padding: "12px", fontSize: "11px", width: "22.5%" }}>{trip.endTime}</td>
                  <td style={{ padding: "12px", width: "22.5%" }}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      onClick={(e) => handleLocationClick(e, eLat, eLng)}
                      sx={{ "&:hover": { opacity: 0.7 } }}
                    >
                      <LocationOnIcon sx={{ fontSize: "14px", color: "#F44335" }} />
                      <MDTypography variant="caption" sx={{ fontSize: "11px", color: "#F44335", fontWeight: "medium" }}>
                        View End
                      </MDTypography>
                    </MDBox>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </MDBox>
  );
};

export default SpeedAnalysis;